import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Command,
  ArrowRight,
  Home,
  User,
  Bell,
  Wallet,
  Store,
  Calendar,
  Settings,
  Plus,
  Users,
  Clock3,
  Sparkles,
  Star,
  X,
  type LucideIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CommandPaletteItem {
  id: string;
  label: string;
  description: string;
  category: string;
  icon?: string;
  action: () => void;
  isRecent?: boolean;
  isContextual?: boolean;
  isFavorite?: boolean;
}

export interface CommandPaletteProps {
  isOpen?: boolean;
  onClose?: () => void;
  query?: string;
  onQueryChange?: (query: string) => void;
  items?: CommandPaletteItem[];
  onSelect?: (item: CommandPaletteItem) => void;
  favoriteCommandIds?: string[];
  onToggleFavorite?: (commandId: string) => void;
}

const categoryIcons: Record<string, React.ElementType> = {
  navigation: Home,
  actions: Plus,
  social: Users,
  tools: Settings,
  custom: Star,
};

const categoryColors: Record<string, string> = {
  navigation: 'text-blue-400',
  actions: 'text-green-400',
  social: 'text-violet-400',
  tools: 'text-amber-400',
  custom: 'text-rose-400',
};

const itemIcons: Record<string, React.ElementType> = {
  feed: Home,
  profile: User,
  notifications: Bell,
  wallet: Wallet,
  marketplace: Store,
  events: Calendar,
  settings: Settings,
  group: Users,
};

export function CommandPalette({
  isOpen = false,
  onClose,
  query = '',
  onQueryChange,
  items = [],
  onSelect,
  favoriteCommandIds = [],
  onToggleFavorite
}: CommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Group items by category
  const groupedItems = items.reduce<Record<string, CommandPaletteItem[]>>((acc, item) => {
    const cat = item.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Reset selected index when query or items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, items.length]);

  // Focus input when opened and safely manage scroll
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow || '';
      };
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (items.length > 0 ? (prev + 1) % items.length : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (items.length > 0 ? (prev - 1 + items.length) % items.length : 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (items[selectedIndex]) {
          handleSelect(items[selectedIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, items, selectedIndex, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleSelect = (item: CommandPaletteItem) => {
    if (onSelect) {
      onSelect(item);
    } else {
      item.action();
    }
    onClose?.();
  };

  const recentCount = items.filter((item) => item.isRecent).length;
  const favoriteCount = items.filter((item) => item.isFavorite || favoriteCommandIds.includes(item.id)).length;
  const contextualCount = items.filter((item) => item.isContextual).length;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[999999] flex items-start justify-center pt-20 px-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Palette Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -10 }}
            transition={{ duration: 0.15 }}
            className="relative w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden z-10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Input */}
            <div className="flex items-center px-4 border-b border-slate-100 dark:border-slate-800">
              <Search className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => onQueryChange?.(e.target.value)}
                placeholder="Type a command or search... (Ctrl+K or ⌘K)"
                className="w-full py-4 text-sm bg-transparent border-0 outline-none placeholder:text-slate-400 text-slate-900 dark:text-white"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => onQueryChange?.('')}
                  className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 border-b border-slate-100/80 px-4 py-2 text-[11px] dark:border-slate-800/80">
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300 font-medium">
                <Star className="h-3 w-3" />
                {favoriteCount} favorite
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-500 dark:bg-slate-800 dark:text-slate-300 font-medium">
                <Clock3 className="h-3 w-3" />
                {recentCount} recent
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300 font-medium">
                <Sparkles className="h-3 w-3" />
                {contextualCount} recommended
              </span>
            </div>

            {/* Items list */}
            <div ref={listRef} className="max-h-80 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-blue-500">
              {items.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-400">
                  No commands found
                </div>
              ) : (
                (() => {
                  let runningIndex = 0;
                  return Object.entries(groupedItems).map(([category, catItems]) => {
                    const CatIcon = categoryIcons[category] || Command;
                    const catColor = categoryColors[category] || 'text-slate-400';

                    return (
                      <div key={category} className="mb-2 last:mb-0">
                        <div className="flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                          <CatIcon className={cn('w-3 h-3', catColor)} />
                          <span>{category}</span>
                        </div>
                        {catItems.map((item) => {
                          const itemIndex = runningIndex;
                          runningIndex += 1;
                          const isSelected = itemIndex === selectedIndex;
                          const isFavorite = item.isFavorite || favoriteCommandIds.includes(item.id);
                          const descWord = item.description.toLowerCase().split(' ').pop() || '';
                          const ItemIcon = itemIcons[descWord] || ArrowRight;

                          return (
                            <div
                              key={item.id}
                              data-index={itemIndex}
                              onClick={() => handleSelect(item)}
                              onMouseEnter={() => setSelectedIndex(itemIndex)}
                              className={cn(
                                'flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer text-sm transition-colors',
                                isSelected
                                  ? 'bg-blue-600 text-white font-medium shadow-sm'
                                  : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/60'
                              )}
                            >
                              <ItemIcon className={cn(
                                'w-4 h-4 flex-shrink-0',
                                isSelected ? 'text-white' : 'text-slate-400'
                              )} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{item.description}</p>
                                <div className="mt-0.5 flex items-center gap-2">
                                  {isFavorite ? (
                                    <p className={cn('text-[11px]', isSelected ? 'text-amber-200' : 'text-amber-500 dark:text-amber-300')}>Favorite</p>
                                  ) : null}
                                  {item.isRecent ? (
                                    <p className={cn('text-[11px]', isSelected ? 'text-emerald-200' : 'text-emerald-500 dark:text-emerald-300')}>Recent</p>
                                  ) : null}
                                  {item.isContextual ? (
                                    <p className={cn('text-[11px]', isSelected ? 'text-blue-200' : 'text-blue-500 dark:text-blue-300')}>Recommended</p>
                                  ) : null}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onToggleFavorite?.(item.id);
                                  }}
                                  className={cn(
                                    'rounded-md p-1 transition-colors',
                                    isFavorite
                                      ? 'text-amber-400 hover:bg-amber-50/20'
                                      : 'text-slate-300 hover:text-amber-500 hover:bg-slate-100 dark:text-slate-600 dark:hover:text-amber-300 dark:hover:bg-slate-800'
                                  )}
                                  aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                                >
                                  <Star className={cn('h-3.5 w-3.5', isFavorite ? 'fill-current' : '')} />
                                </button>
                                <kbd className={cn(
                                  'rounded-md px-1.5 py-0.5 text-[10px] font-semibold',
                                  isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                                )}>
                                  {item.label}
                                </kbd>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  });
                })()
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-400">
              <div className="flex items-center gap-2">
                <span>Navigate: <kbd className="px-1 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">↑</kbd> <kbd className="px-1 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">↓</kbd></span>
                <span>Select: <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">↵</kbd></span>
              </div>
              <div>
                <span>Close: <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">ESC</kbd></span>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default CommandPalette;